import { useEffect, useRef } from 'react';
import { deleteDraftOrder } from '../api/business-central';
import { CartDataItems, CartPageType } from './models/cart';
import { BcOrderCart } from './models/order';
import { format, parse } from 'date-fns';
import { brochureCartHash, iconClassPrefix, legacyIconClassPrefix, orderedSizesArray } from './constants';
import { SearchOrderLine } from './models/documents';
import { de, enGB, es, fr, it } from 'date-fns/locale';
import { CartPageTypes } from './enum/B2bCartSection';
import { iconNames } from './types/icon.type';

type Timer = ReturnType<typeof setTimeout>;
type SomeFunction = (...args: any[]) => void;

export function useDebounce<Func extends SomeFunction>(func: Func, delay = 1000) {
    const timer = useRef<Timer>();

    useEffect(() => {
        return () => {
            if (!timer.current) return;
            clearTimeout(timer.current);
        };
    }, []);

    const debouncedFunction = ((...args) => {
        const newTimer = setTimeout(() => {
            func(...args);
        }, delay);
        clearTimeout(timer.current);
        timer.current = newTimer;
    }) as Func;

    return debouncedFunction;
}

export const usePrevious = (value: any) => {
    const currentRef = useRef(value);
    const previousRef = useRef();
    if (currentRef.current !== value) {
        previousRef.current = currentRef.current;
        currentRef.current = value;
    }
    return previousRef.current;
};

export const snakeToCamel = (str: string): string => {
    return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
};

export const camelToSnakeCase = (str: string) => {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export const removeUndefinedAndNullFromObject = (object: Record<string, unknown>): Record<string, unknown> => {
    if (object) {
        Object.keys(object).forEach((key: string) =>
            object[key] === undefined || object[key] === null || object[key] === '' ? delete object[key] : '',
        );
    }
    return object;
};

export const convertNumberToCurrency = (price: number, currencyCode: string = ''): string => {
    const formatter = new Intl.NumberFormat('it-IT', {
        style: currencyCode ? 'currency' : 'decimal',
        minimumFractionDigits: 2,
        ...(!!currencyCode ? { currency: currencyCode } : {})
    });
    return `${formatter.format(price)}`;
};

export const convertToTitleCase = (str: string) => {
    if (str === null || str === undefined) {
        return '';
    }

    return str
        .toLowerCase()
        .split(' ')
        .map(function (word) {
            return word.charAt(0).toUpperCase().concat(word.substr(1));
        })
        .join(' ');
};

export const checkAndAddEvents = () => {
    for (const dropdown of document.querySelectorAll('#AccountPage .select-wrapper')) {
        dropdown.addEventListener('click', function () {
            const select = dropdown.querySelector('.select');
            if (select) {
                select.classList.toggle('open');
            }
        });
        for (const option of dropdown.querySelectorAll('.custom-option')) {
            option.addEventListener('click', function () {
                if (option) {
                    if (!option.classList.contains('selected') && option.parentNode) {
                        const customOptionSelected = option.parentNode.querySelector('.custom-option.selected');
                        if (customOptionSelected) {
                            customOptionSelected.classList.remove('selected');
                        }
                        option.classList.add('selected');
                        if (option.closest('.select')) {
                            const selectTrigger = option.closest('.select')!.querySelector('.select__trigger span');
                            if (selectTrigger) {
                                selectTrigger.textContent = option.textContent;
                            }
                        }
                    }
                }
            });
        }
    }
};

export const clearCart = (reload = true) => {
    if (localStorage.getItem('errea_cartProducts')) {
        localStorage.removeItem('errea_cartProducts');
    }
};

export const clearAllErreaLocalStorage = async () => {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
        if (key.includes('errea_')) {
            if (key == 'errea_promoOrderCalc' || key == 'errea_associatedOrderCalc') {
                const ordersData: BcOrderCart[] = JSON.parse(localStorage.getItem(key) as string) ?? [];
                for (const order of ordersData) {
                    await deleteDraftOrder(order.documentNo);
                }
            }
            localStorage.removeItem(key);
        }
    }
};

export const handleLogout = async () => {
    clearAllErreaLocalStorage();
    window.location.replace(
        `${window.errea_accountUserData.language != 'it' ? `/${window.errea_accountUserData.language}` : ''
        }/account/logout`,
    );
};

export const sortProductsArrayPerSize = (variants: CartDataItems[]) => {
    const sortedVariants = variants.sort((p1, p2) =>
        p1.variantId > p2.variantId ? 1 : p1.variantId < p2.variantId ? -1 : 0,
    );
    return sortedVariants;
};

export const sortProductArrayBySize = (items: CartDataItems[]): CartDataItems[] => {
    const sizeIndexMap: { [key: string]: number } = orderedSizesArray.reduce((map, sizeCode, index) => {
        map[sizeCode] = index;
        return map;
    }, {} as { [key: string]: number });

    // Funzione di confronto per il metodo sort
    const compareSizes = (a: CartDataItems, b: CartDataItems): number => {
        return sizeIndexMap[a.options[1].value] - sizeIndexMap[b.options[1].value];
    };

    // Ordinare l'array di oggetti
    items.sort(compareSizes);

    return items;
};

export const removeSizeFromSku = (sku: string): string => {
    const skuSplitted = sku.split('-');
    if (skuSplitted[0]) {
        return skuSplitted[0];
    } else {
        return sku;
    }
};

export const getLocalizedBaseUrl = (): string => {
    return (
        document.location.origin +
        (window.errea_accountUserData.language !== 'it' ? `/${window.errea_accountUserData.language}` : '')
    );
};

export const formatDate = (date: string): string => {
    if (date) {
        const dateParsed = parse(date, 'yyyy-MM-dd', new Date());
        const result = format(dateParsed, 'dd/MM/yyyy');
        return result;
    }
    return '';
};

export const formatDateMonth = (date: string, localeCode?: string): string => {
    if (date) {
        const dateParsed = parse(date, 'yyyy-MM-dd', new Date());
        let locale = it;
        switch (localeCode) {
            case 'es':
                locale = es;
                break;
            case 'de':
                locale = de;
                break;
            case 'fr':
                locale = fr;
                break;
            case 'en':
                locale = enGB;
                break;
            default:
                locale = it;
                break;
        }
        const result = format(dateParsed, 'MMM yyyy', { locale: locale });
        return result.toUpperCase();
    }
    return '';
};

export const sortArrayBySize = (items: SearchOrderLine[]): SearchOrderLine[] => {
    const sizeIndexMap: { [key: string]: number } = orderedSizesArray.reduce((map, sizeCode, index) => {
        map[sizeCode] = index;
        return map;
    }, {} as { [key: string]: number });

    // Funzione di confronto per il metodo sort
    const compareSizes = (a: SearchOrderLine, b: SearchOrderLine): number => {
        return sizeIndexMap[a.sizeCode] - sizeIndexMap[b.sizeCode];
    };

    // Ordinare l'array di oggetti
    items.sort(compareSizes);

    return items;
};

export const extractOrderNumberError = (error: string) => {
    const regex = /No\.\=(.*?)\./;
    const match = error.match(regex);
    if (match) {
        return match[1].trim();
    }
    return null;
};

export const getCartPageType: () => CartPageType = () => {
    switch (window.location.hash.split('?')[0]) {
        case '#promo':
            return CartPageTypes.PROMO;
        case `#${brochureCartHash}`:
            return CartPageTypes.BROCHURE;
        case '#thankyou':
            return CartPageTypes.THANKYOU;
        default:
            return CartPageTypes.CATALOG;
    }
};

export const parseQuantity = (value: string | number | null): number => {
    const parsedValue = parseInt(value?.toString() ?? '', 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const getOptionIndex = (optionNames: string[], optionName: string = 'taglia') =>
    optionNames.findIndex((name) => name.toLowerCase() === optionName.toLowerCase());

export const getOptionValue = (
    optionIndex: number,
    variant: { option1?: string | null; option2?: string | null; option3?: string | null },
    defaultValue: string = '',
) => {
    if (optionIndex === 0) return variant.option1 ?? defaultValue;
    if (optionIndex === 1) return variant.option2 ?? defaultValue;
    if (optionIndex === 2) return variant.option3 ?? defaultValue;

    return defaultValue;
};

export const getIconPrefix = (iconName: string): string => {
    if (!iconName) {
        return '';
    }

    const isNewIcon = iconNames.some(newIcon => newIcon === iconName);
    return isNewIcon ? iconClassPrefix : legacyIconClassPrefix;
}

export const productIgnoresStockCheck = (product: Pick<CartDataItems, 'deliveryPeriod' | 'pfsDeliveryItem'>): boolean => {
    if (
        (product.deliveryPeriod === 'CA0' || product.deliveryPeriod === 'SP0') &&
        product.pfsDeliveryItem === false
    ) {
        return true;
    }


    return false;
}
